import Swal from "sweetalert2";
import { apiV2, errorHandler } from "./index";
import {
  API_ERROR,
  FETCH_CANCELLATION_REASONS,
  FETCH_USER_CANCELLATION_REPORT,
  FETCH_CANCELLATION_DISTRIBUTION_PENDING,
  FETCH_CANCELLATION_DISTRIBUTION_SUCCESS,
  FETCH_CANCELLATION_DISTRIBUTION_ERROR,
  FETCH_CANCELLATION_OVER_TIME_PENDING,
  FETCH_CANCELLATION_OVER_TIME_SUCCESS,
  FETCH_CANCELLATION_OVER_TIME_ERROR,
} from "../constants/ActionTypes";
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../constants/pagination';

export function fetchAllcancellationReasons() {
  return function (dispatch) {
    apiV2.get(`/cancellation-reasons`)
      .then((response) => {
        dispatch({
          type: FETCH_CANCELLATION_REASONS,
          payload: response.data.objects,
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function addEditCancellationReasons(jsonOrArray) {
  return function (dispatch) {
    // Handle batch update (array of items with updated sort_order)
    if (Array.isArray(jsonOrArray)) {
      const requests = jsonOrArray.map((item, index) => {
        const isNew = String(item.cancellation_id).startsWith('new-');
        const payload = { reason: item.reason, sort_order: index };
        return isNew
          ? apiV2.post(`/cancellation-reasons`, payload)
          : apiV2.put(`/cancellation-reasons/${item.cancellation_id}`, payload);
      });

      return Promise.all(requests)
        .then(() => {
          dispatch(fetchAllcancellationReasons());
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            title: error.response?.data?.message || "Error saving",
            heightAuto: true,
          });
          throw error;
        });
    }

    // Handle single item create/update
    const isNew = !jsonOrArray.cancellation_id || String(jsonOrArray.cancellation_id).startsWith('new-');
    const request = isNew
      ? apiV2.post(`/cancellation-reasons`, jsonOrArray)
      : apiV2.put(`/cancellation-reasons/${jsonOrArray.cancellation_id}`, jsonOrArray);

    return request
      .then((response) => {
        dispatch(fetchAllcancellationReasons());
        return response.data;
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: error.response?.data?.message || "Error saving",
          heightAuto: true,
        });
        throw error;
      });
  };
}

export function deleteCancellationReasons(cancellation_id) {
  return function (dispatch) {
    return apiV2.delete(`/cancellation-reasons/${cancellation_id}`)
      .then((response) => {
        Swal.fire({
          icon: "success",
          title: "Deleted Successfully!",
          timer: 1000,
        }).then(() => {
          dispatch(fetchAllcancellationReasons());
        });
      })
      .catch((error) => {

        Swal.fire({
          icon: "error",
          title: error.response.data.message,
          heightAuto: true,
        });
      });
  };
}

/**
 * Fetch the aggregated cancellation distribution for a chosen period.
 * Powers the chart on the Cancellations Report page (Monday 11962034770).
 *
 * @param {Object} options
 * @param {('current_calendar_year'|'last_month'|'last_30_days'|'year'|'all_time')} options.period
 * @param {number} [options.year] required when period === 'year'
 * @param {boolean} [options.compare] include a previous-period block in the response
 */
export function fetchCancellationDistribution({ period, year, compare = false }) {
  return function (dispatch) {
    dispatch({ type: FETCH_CANCELLATION_DISTRIBUTION_PENDING });
    const params = { period };
    if (year != null) params.year = year;
    if (compare) params.compare = 1;
    return apiV2.get(`/user-cancellations/distribution`, { params })
      .then((response) => {
        dispatch({
          type: FETCH_CANCELLATION_DISTRIBUTION_SUCCESS,
          payload: response.data,
        });
      })
      .catch((error) => {
        dispatch({ type: FETCH_CANCELLATION_DISTRIBUTION_ERROR });
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

/**
 * Monthly cancellation totals for the rolling last 12 months with the
 * matching calendar month from one year prior overlaid in the same
 * bucket. Independent of the page-level period selector — provides an
 * evergreen "trending up/down vs last year" view for the time-series
 * chart on the Cancellations Report.
 */
export function fetchCancellationOverTime() {
  return function (dispatch) {
    dispatch({ type: FETCH_CANCELLATION_OVER_TIME_PENDING });
    return apiV2.get(`/user-cancellations/over-time`)
      .then((response) => {
        dispatch({
          type: FETCH_CANCELLATION_OVER_TIME_SUCCESS,
          payload: response.data,
        });
      })
      .catch((error) => {
        dispatch({ type: FETCH_CANCELLATION_OVER_TIME_ERROR });
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

/**
 * @param {number} page
 * @param {number} perPage
 * @param {Object} [filters] optional period filter — keeps the raw-events
 *   drill-down in lockstep with the distribution chart's period selector.
 *   `period` is one of UserCancellationService::SUPPORTED_PERIODS in APIv2;
 *   `year` is required when period === 'year'.
 * @param {string} [filters.period]
 * @param {number} [filters.year]
 */
export function fetchUserCancellationReports(page = 1, perPage = DEFAULT_PER_PAGE, filters = {}) {
  return function (dispatch) {
    const params = { page, page_size: perPage };
    if (filters.period) params.period = filters.period;
    if (filters.year != null) params.year = filters.year;
    apiV2.get(`/user-cancellations`, { params })
      .then((response) => {
        dispatch({
          type: FETCH_USER_CANCELLATION_REPORT,
          payload: {
            data: response.data.data || [],
            total: response.data.total_count || 0,
            page: response.data.page || 1,
            page_size: perPage,
            total_pages: response.data.page_count || 0
          }
        });
      })
      .catch((error) => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}
